import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Package,
  PlusCircle,
  ShoppingCart,
  MessageSquareQuote,
  Layers,
  Settings,
  Image,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Search,
  Bell,
  Upload,
  Users,
  QrCode,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../../stores/authStore';
import { useAdminStats } from '../../services/adminService';
import { AdminSearchModal } from '../AdminSearchModal/AdminSearchModal';

const SIDEBAR_LINKS = [
  { label: 'Dashboard', to: '/admin', icon: LayoutDashboard, end: true },
  { label: 'Products', to: '/admin/products', icon: Package },
  { label: 'Add Product', to: '/admin/products/new', icon: PlusCircle },
  { label: 'Orders', to: '/admin/orders', icon: ShoppingCart },
  // { label: 'QR Codes', to: '/admin/qr-codes', icon: QrCode },
  { label: 'Import / Export', to: '/admin/import', icon: Upload },
  { label: 'Testimonials', to: '/admin/testimonials', icon: MessageSquareQuote },
  { label: 'Team', to: '/admin/team', icon: Users },
  { label: 'Categories', to: '/admin/categories', icon: Layers },
  { label: 'Media', to: '/admin/media', icon: Image },
  { label: 'Settings', to: '/admin/settings', icon: Settings },
];

/**
 * Admin layout — persistent sidebar + top header + content area.
 */
export function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuthStore();
  const { data: stats } = useAdminStats();

  // Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handleKb = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handleKb);
    return () => window.removeEventListener('keydown', handleKb);
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate('/admin/login', { replace: true });
  };

  const isActive = (link) => {
    if (link.end) return location.pathname === link.to;
    return location.pathname.startsWith(link.to);
  };

  /* ── Sidebar content (shared desktop/mobile) ── */
  const sidebarContent = (isMobile = false) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={clsx(
        'flex items-center h-16 px-4 border-b border-ui-border flex-shrink-0',
        collapsed && !isMobile ? 'justify-center' : 'gap-3'
      )}>
        {(!collapsed || isMobile) && (
          <Link to="/admin" className="text-2xl font-extrabold text-brand-dark italic tracking-tight">
            smothers
          </Link>
        )}
        {collapsed && !isMobile && (
          <Link to="/admin" className="text-xl font-extrabold text-brand-red italic">
            S
          </Link>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {SIDEBAR_LINKS.map((link) => {
          const Icon = link.icon;
          const active = isActive(link);
          return (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => isMobile && setMobileOpen(false)}
              className={clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all group relative',
                active
                  ? 'bg-brand-red text-brand-white shadow-md'
                  : 'text-brand-gray hover:bg-brand-light hover:text-brand-dark'
              )}
            >
              {/* Active indicator bar */}
              {active && (
                <motion.div
                  layoutId="admin-sidebar-indicator"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-brand-white rounded-r-full"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <Icon size={18} className={active ? 'text-brand-white' : 'text-brand-gray group-hover:text-brand-dark'} />
              {(!collapsed || isMobile) && <span>{link.label}</span>}

              {/* Order badge */}
              {link.label === 'Orders' && stats?.orderCount > 0 && (!collapsed || isMobile) && (
                <span className={clsx(
                  'ml-auto px-2 py-0.5 text-xs font-bold rounded-full',
                  active ? 'bg-brand-white/20 text-brand-white' : 'bg-brand-red/10 text-brand-red'
                )}>
                  {stats.orderCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-ui-border flex-shrink-0">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-brand-gray hover:bg-red-50 hover:text-error transition-all"
        >
          <LogOut size={18} />
          {(!collapsed || isMobile) && <span>Logout</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-ui-background">
      {/* ── Desktop sidebar ── */}
      <motion.aside
        animate={{ width: collapsed ? 72 : 256 }}
        transition={{ duration: 0.2 }}
        className="hidden lg:flex flex-col flex-shrink-0 bg-brand-white border-r border-ui-border h-screen sticky top-0 z-30"
      >
        {sidebarContent(false)}

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-brand-white border border-ui-border rounded-full flex items-center justify-center shadow-sm hover:bg-brand-light transition-colors z-40"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </motion.aside>

      {/* ── Mobile sidebar overlay ── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 bg-brand-dark/50 z-40 lg:hidden"
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed left-0 top-0 bottom-0 w-[280px] bg-brand-white z-50 lg:hidden shadow-2xl"
            >
              <button
                onClick={() => setMobileOpen(false)}
                className="absolute top-4 right-4 p-1.5 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
              {sidebarContent(true)}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Main content area ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="h-16 bg-brand-white border-b border-ui-border flex items-center px-4 sm:px-6 gap-4 sticky top-0 z-20">
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>

          {/* Search trigger (desktop) */}
          <div className="hidden sm:flex items-center flex-1 max-w-md">
            <button
              onClick={() => setSearchOpen(true)}
              className="relative w-full flex items-center gap-2 pl-9 pr-4 py-2 text-sm bg-brand-light rounded-xl text-ui-muted hover:ring-2 hover:ring-brand-red/20 transition-all text-left"
            >
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
              Search...
              <kbd className="ml-auto hidden lg:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono font-bold text-ui-muted bg-brand-white border border-ui-border rounded">
                ⌘K
              </kbd>
            </button>
          </div>

          {/* Quick stats + actions */}
          <div className="flex items-center gap-3 ml-auto">
            {/* Quick Add Product */}
            <Link
              to="/admin/products/new"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-red hover:bg-red-700 text-brand-white text-xs font-bold rounded-lg transition-colors"
            >
              <PlusCircle size={14} />
              Quick Add
            </Link>

            {/* Notification bell */}
            <button className="relative p-2 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors">
              <Bell size={18} />
              {stats?.orderCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-brand-red text-brand-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {stats.orderCount > 9 ? '9+' : stats.orderCount}
                </span>
              )}
            </button>

            {/* Avatar */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-brand-red/10 flex items-center justify-center text-sm font-bold text-brand-red">
                {user?.email?.charAt(0).toUpperCase() || 'A'}
              </div>
              <span className="hidden md:block text-xs font-medium text-brand-dark max-w-[120px] truncate">
                {user?.email || 'Admin'}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Search Modal */}
      <AdminSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
